import { type SerializedCompStructure } from '@wix/document-services-types'
import { entries } from '@wix/wix-data-client-common-standalone'

const uniquifyIdAndMetaDataSig = <Target extends object>({
  target,
  uniquifyIdOrSig,
}: {
  target: Target
  uniquifyIdOrSig: (idOrSig: string) => string
}): Target => ({
  ...target,

  ...('id' in target && typeof target.id === 'string'
    ? { id: uniquifyIdOrSig(target.id) }
    : null),

  ...('metaData' in target &&
  typeof target.metaData === 'object' &&
  target.metaData !== null &&
  'sig' in target.metaData &&
  typeof target.metaData.sig === 'string'
    ? {
        metaData: {
          ...target.metaData,
          sig: uniquifyIdOrSig(target.metaData.sig),
        },
      }
    : null),
})

export const copyComponent = ({
  component,
  uniquifyIdOrSig,
}: {
  component: SerializedCompStructure
  uniquifyIdOrSig: (idOrSig: string) => string
}): SerializedCompStructure =>
  uniquifyIdAndMetaDataSig({
    target: {
      ...component,

      ...(component.components !== undefined
        ? {
            components: component.components.map(subComponent =>
              copyComponent({ component: subComponent, uniquifyIdOrSig }),
            ),
          }
        : null),

      ...(component.connections !== undefined
        ? {
            connections: uniquifyIdAndMetaDataSig({
              target: component.connections,
              uniquifyIdOrSig,
            }),
          }
        : null),

      ...(component.data !== undefined
        ? {
            data: uniquifyIdAndMetaDataSig({
              target: component.data,
              uniquifyIdOrSig,
            }),
          }
        : null),

      ...('editorsettings' in component &&
      typeof component.editorsettings === 'object' &&
      component.editorsettings !== null
        ? {
            editorsettings: uniquifyIdAndMetaDataSig({
              target: component.editorsettings,
              uniquifyIdOrSig,
            }),
          }
        : null),

      ...(component.layouts !== undefined
        ? {
            layouts: uniquifyIdAndMetaDataSig({
              target: component.layouts,
              uniquifyIdOrSig,
            }),
          }
        : null),

      ...(component.props !== undefined
        ? {
            props: uniquifyIdAndMetaDataSig({
              target: component.props,
              uniquifyIdOrSig,
            }),
          }
        : null),

      ...(component.scopedLayouts !== undefined
        ? {
            scopedLayouts: Object.fromEntries(
              entries(component.scopedLayouts).map(
                ([breakpointId, scopedLayout]) => [
                  breakpointId,
                  uniquifyIdAndMetaDataSig({
                    target: scopedLayout,
                    uniquifyIdOrSig,
                  }),
                ],
              ),
            ),
          }
        : null),

      ...(component.scopedStyles !== undefined
        ? {
            scopedStyles: Object.fromEntries(
              entries(component.scopedStyles).map(
                ([breakpointId, scopedStyle]) => [
                  breakpointId,
                  uniquifyIdAndMetaDataSig({
                    target: scopedStyle,
                    uniquifyIdOrSig,
                  }),
                ],
              ),
            ),
          }
        : null),

      ...(typeof component.style === 'object' && component.style !== null
        ? {
            style: uniquifyIdAndMetaDataSig({
              target: component.style,
              uniquifyIdOrSig,
            }),
          }
        : null),
    },
    uniquifyIdOrSig,
  })
